import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
  Stack,
  GridLegacy as Grid,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { useIntersection } from "../../components/Forms/hooks";
import { AvailabilityCalendar } from "../Availability/AvailabilityCalendar";
import { usePickCoach } from "./api";
import { CoachInfo } from "./CoachInfo";
import { ContactModal } from "./ContactModal";
import { IntroLink } from "./IntroLink";
import { formatName, getCoachPhotoUrl } from "./coach.utils";
import { LayoutCtx } from "../../components/Layout";
import { useContext } from "react";
import { CoachCardBtns } from "./CoachCardBtns";
import { useAuth } from "../Authorization";

export const CoachCard = ({
  coach,
  withContact,
  isLoading,
  sx = { mb: 3 },
}) => {
  const { rightOpen: rightMenuExpanded } = useContext(LayoutCtx);
  const { username, webLink } = coach || {};
  const [contactCoach, setContactCoach] = useState(null);
  const [wasVisible, setWasVisible] = useState(false);
  const handleContact = useCallback(() => setContactCoach(coach), [coach]);
  const pickCoach = usePickCoach({ coach });
  const { user } = useAuth();
  const pickedCoach = user.data.coach;
  const elementRef = useRef();
  useIntersection({
    elementRef,
    onVisibleOnce: () => setWasVisible(true),
  });
  const [calendarVisible, setCalendarVisible] = useState(false);
  // console.log("[CoachCard.rndr]", name, { isVisible, coach, wasVisible });
  return (
    <>
      <Card sx={{ ...sx }} ref={elementRef}>
        <CardContent sx={{ padding: "24px" }}>
          {calendarVisible ? (
            <Grid //full card
              display="flex"
              container
              spacing={2}
              //alignItems="center"
              alignItems={{ xs: "center", sm: "flex-start", xl: "stretch" }}
              flexDirection={{ xs: "column", sm: "row", xl: "row" }} // XS = stacked (sloupec), SM+ = row
            >
              <Grid //photo
                item
                xs={12}
                sm={3}
                md={4}
                lg={3}
                xl={3}
                display="flex"
                justifyContent="flex-start"
                alignItems="flex-start"
                flexDirection={{ xs: "row", xl: "column" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 0.6,
                    aspectRatio: "1 / 1.2",
                    overflow: "hidden",
                    minWidth: { xs: 100, xl: 120 },
                    minHeight: 200,
                    width: rightMenuExpanded
                      ? { xs: "auto", xl: 220 }
                      : { xs: "auto", sm: 200, md: 350, lg: 350, xl: 250 },
                    height: rightMenuExpanded
                      ? { xs: "auto", md: 400, lg: 250, xl: 350 }
                      : { xs: "auto", sm: 250, md: 400, lg: 350, xl: 350 },
                    maxHeight: 500,
                    textAlign: "center",
                    marginTop: { xs: 0, sm: 1.5, md: 1.5, lg: 1.5, xl: 1.5 },
                    marginBottom: { xs: 0, sm: 1.5, md: 1.5 },
                    marginLeft: { xs: 1.5, md: 1, xl: 1.5 },
                  }}
                >
                  {isLoading ? (
                    <Skeleton
                      variant="rectangular"
                      sx={{ minHeight: 300, width: "100%", height: "100%" }}
                    />
                  ) : (
                    <>
                      <CardMedia
                        component="img"
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 0.6,
                          objectFit: "cover",
                        }}
                        image={
                          wasVisible ? getCoachPhotoUrl(username) : undefined
                        }
                        alt="profile photo"
                      />
                      <IntroLink webLink={webLink} />
                    </>
                  )}
                </Box>
              </Grid>
              <Grid //coach info
                item
                xs={12}
                sm={9}
                md={8}
                lg={5}
                xl={4.5}
                display="flex"
                justifyContent={{ xs: "center", sm: "flex-start" }}
                flexDirection={{ xs: "row", xl: "column" }}
              >
                {isLoading ? (
                  <Stack useFlexGap sx={{ width: "100%" }}>
                    <Skeleton
                      variant="text"
                      sx={{ height: 24, mb: 1, width: "40%" }}
                    />
                    <Skeleton variant="text" sx={{ width: "90%" }} />
                    <Skeleton variant="text" sx={{ width: "70%" }} />
                    <Skeleton variant="text" sx={{ width: "90%" }} />
                    <Skeleton variant="text" sx={{ width: "30%" }} />
                  </Stack>
                ) : (
                  <ErrorBoundary>
                    <CoachInfo
                      coach={{
                        ...coach,
                        name: formatName(coach),
                      }}
                      maxBioChars={250}
                      isLoading={isLoading}
                      withFavorite
                      sx={{
                        flexGrow: 1,
                        width: "100%",
                        padding: "10px",
                        height: "100%",
                      }}
                    />
                  </ErrorBoundary>
                )}
              </Grid>
              {calendarVisible && (
                <Grid item xs={12} lg={4} xl={4.5}>
                  <ErrorBoundary>
                    {!isLoading && (
                      <AvailabilityCalendar
                        coach={coach}
                        // onContact={withContact && handleContact}
                        // onPick={pickCoach.onPick}
                        // pickPending={pickCoach.pickPending}
                        // fetchDisabled={!wasVisible}
                        sx={{
                          alignSelf: { xs: "auto", md: "center" },
                          height: { xs: "auto", lg: 350 },
                          marginTop: { xs: 0, lg: 1.5, xl: 1.5 },
                        }}
                      />
                    )}
                  </ErrorBoundary>
                </Grid>
              )}{" "}
            </Grid>
          ) : (
            <Grid //full card
              display="flex"
              container
              spacing={2}
              //alignItems="center"
              alignItems={{ xs: "center", sm: "flex-start", xl: "stretch" }}
              flexDirection={{ xs: "column", sm: "row", xl: "row" }} // XS = stacked (sloupec), SM+ = row
            >
              <Grid //photo
                item
                xs={12}
                sm={3}
                md={3}
                lg={3}
                xl={3}
                display="flex"
                justifyContent="flex-start"
                alignItems="flex-start"
                flexDirection={{ xs: "row", xl: "row" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 0.6,
                    aspectRatio: "1 / 1.2",
                    overflow: "hidden",
                    minWidth: { xs: 100, xl: 120 },
                    minHeight: 200,
                    width: rightMenuExpanded
                      ? { xs: "auto", xl: 220 }
                      : { xs: "auto", sm: 200, md: 350, lg: 350, xl: 250 },
                    height: rightMenuExpanded
                      ? { xs: "auto", md: 400, lg: 250, xl: 350 }
                      : { xs: "auto", sm: 250, md: 400, lg: 350, xl: 350 },
                    maxHeight: 500,
                    textAlign: "center",
                    marginTop: { xs: 0, sm: 1.5, md: 1.5, lg: 1.5, xl: 1.5 },
                    marginBottom: { xs: 0, sm: 1.5, md: 1.5 },
                    marginLeft: { xs: 1.5, md: 1, xl: 1.5 },
                  }}
                >
                  {isLoading ? (
                    <Skeleton
                      variant="rectangular"
                      sx={{ minHeight: 300, width: "100%", height: "100%" }}
                    />
                  ) : (
                    <>
                      <CardMedia
                        component="img"
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 0.6,
                          objectFit: "cover",
                        }}
                        image={
                          wasVisible ? getCoachPhotoUrl(username) : undefined
                        }
                        alt="profile photo"
                      />
                      <IntroLink webLink={webLink} />
                    </>
                  )}
                </Box>
              </Grid>
              <Grid //coach info
                item
                xs={12}
                sm={9}
                md={9}
                lg={9}
                xl={9}
                display="flex"
                justifyContent={{ xs: "center", sm: "flex-start" }}
                flexDirection={{ xs: "row", xl: "column" }}
              >
                {isLoading ? (
                  <Stack useFlexGap sx={{ width: "100%" }}>
                    <Skeleton
                      variant="text"
                      sx={{ height: 24, mb: 1, width: "40%" }}
                    />
                    <Skeleton variant="text" sx={{ width: "90%" }} />
                    <Skeleton variant="text" sx={{ width: "70%" }} />
                    <Skeleton variant="text" sx={{ width: "90%" }} />
                    <Skeleton variant="text" sx={{ width: "30%" }} />
                  </Stack>
                ) : (
                  <ErrorBoundary>
                    <CoachInfo
                      coach={{
                        ...coach,
                        name: formatName(coach),
                      }}
                      maxBioChars={250}
                      isLoading={isLoading}
                      withFavorite
                      sx={{
                        flexGrow: 1,
                        width: "100%",
                        padding: "10px",
                        height: "100%",
                      }}
                    />
                  </ErrorBoundary>
                )}
              </Grid>
              {calendarVisible && (
                <Grid item>
                  <ErrorBoundary>
                    {!isLoading && (
                      <AvailabilityCalendar
                        coach={coach}
                        sx={{
                          alignSelf: { xs: "auto", md: "center" },
                          height: { xs: "auto", lg: 350 },
                          marginTop: { xs: 0, lg: 1.5, xl: 1.5 },
                        }}
                      />
                    )}
                  </ErrorBoundary>
                </Grid>
              )}{" "}
            </Grid>
          )}
          <CoachCardBtns
            sx={{ mb: 1 }}
            calendarVisible={calendarVisible}
            setCalendarVisible={setCalendarVisible}
            coach={coach}
            pickedCoach={pickedCoach}
            onContact={withContact && handleContact}
            onPick={pickCoach.onPick}
            pickPending={pickCoach.pickPending}
          />
        </CardContent>
      </Card>
      <ContactModal
        coach={contactCoach}
        onClose={() => setContactCoach(null)}
      />
    </>
  );
};
